import { faListAlt } from "@elynx/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Button, Card, CardActions, CardContent, CardHeader, Collapse, Grid, Typography, useTheme } from "@mui/material";
import { format } from "date-fns";
import React, { ReactNode /*, useState*/ } from "react";
import { useTranslation } from "react-i18next";
import { IAttachment } from "../../common/models/IAttachment";
// import { useTranslation } from "react-i18next";
import { getFeetAndInchesText } from "../../common/utilities/FeetAndInches";
// import { useRouteContext } from "../../contexts/route-context/RouteContext";
import ActionMenu from "../action-menu/ActionMenu";
import { ActionMenuItem } from "../action-menu/ActionMenuItem";
import AttachmentList, { AttachmentListCardActionEnum } from "../attachment-list/AttachmentList";
import { RunTicket } from "./RunTicketList";
import { ResourceTypeEnum } from "../../common/enums/ResourceTypeEnum";
import { useUserContext } from "../../contexts/user-context/UserContext";

export enum RunTicketActionEnum {
    Edit = 1,
    Delete = 2,
    ToggleExpansion = 3,
    ViewAttachment = 4,
    DownloadAttachment = 5
}

export type RunTicketActionPayloadType = RunTicket | IAttachment;

export type RunTicketListItemProps = {
    item: RunTicket,
    onAction: (action: RunTicketActionEnum, payload: RunTicketActionPayloadType) => void,
    expanded: boolean;
};

export function RunTicketListItem({
    item,
    onAction,
    expanded
}: RunTicketListItemProps) {
    const theme = useTheme();
    const { t } = useTranslation();
    //const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
    // const { t } = useTranslation();
    // const routeContext = useRouteContext();
    const [userState] = useUserContext();

    let tankLevelEstimatedHaul: number = 0.0;
    let interfaceLevelEstimatedHaul: number = 0.0;

    // TODO: move this into utils, so it can be used by form
    const delta: number = (Number.isFinite(item.tankLevelOpen) &&
        Number.isFinite(item.tankLevelClose))
        ? (item.tankLevelOpen as number) - (item.tankLevelClose as number)
        : 0.0;

    const interfaceDelta: number = (
        item.tank.isDualPhase &&
        Number.isFinite(item.interfaceLevelOpen) &&
        Number.isFinite(item.interfaceLevelClose))
        ? (item.interfaceLevelOpen as number) - (item.interfaceLevelClose as number)
        : 0.0;

    if (item.tankLevelOpen !== null && item.tankLevelClose !== null) {
        const haul: number = (delta - interfaceDelta) * (item.tank?.tankStyle?.inchesMultiplier ?? 0);
        tankLevelEstimatedHaul = formatValue(haul, 0);
    }

    if (item.tank.isDualPhase &&
        Number.isFinite(item.interfaceLevelOpen) &&
        Number.isFinite(item.interfaceLevelClose)) {

        const interfaceHaul: number = interfaceDelta * (item.tank?.tankStyle?.inchesMultiplier ?? 0);
        interfaceLevelEstimatedHaul = formatValue(interfaceHaul, 0);
    }

    function formatValue(value: number, decimals: number): number {
        return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
    }

    function getTitle(): ReactNode {
        return (
            <Box>
                {item.tank.displayText}
            </Box>
        );
    }

    function getSubheader(): ReactNode {
        return (
            <Box>
                {item.createdDateTime &&
                    <Box>
                        {format(new Date(item.createdDateTime), "P pp")}
                    </Box>
                }
                {(item.creator?.length ?? 0) > 0 &&
                    <Box>
                        {item.creator}
                    </Box>
                }
            </Box>
        );
    }

    const actionItems = new Array<ActionMenuItem>(
        {
            label: t("runTicketList.itemMenu.edit"),
            onClick: () => {
                onAction(RunTicketActionEnum.Edit, item);
            },
        },
        {
            label: t("runTicketList.itemMenu.delete"),
            onClick: () => {
                onAction(RunTicketActionEnum.Delete, item);
            },
        }
    );

    function toggleExpanded() {
        onAction(RunTicketActionEnum.ToggleExpansion, item);
    }

    // TODO: All of these special paddings should be handled in theme

    function onAttachmentAction(action: AttachmentListCardActionEnum, payload: IAttachment) {

        if (action === AttachmentListCardActionEnum.View) {
            onAction(RunTicketActionEnum.ViewAttachment, payload);
        } else if (action === AttachmentListCardActionEnum.Download) {
            onAction(RunTicketActionEnum.DownloadAttachment, payload);
        }

    }

    return (
        <Card variant="outlined">
            <CardHeader
                sx={{
                    padding: 1
                }}
                title={getTitle()}
                avatar={<FontAwesomeIcon icon={faListAlt} size="2x" />}
                subheader={getSubheader()}
                action={<ActionMenu actionItems={actionItems} />}
            >
            </CardHeader>
            <CardContent sx={{
                // TODO: Change based on desktop                
                padding: (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important`,
            }}>
                <Typography component="div" variant="body2">
                    <Box>{`${t("runTicket.ticketNumber")}: ${item.ticketNumber}`}</Box>
                    <Box>{`${item.tank.isDualPhase || item.tank.resourceType === ResourceTypeEnum.Oil
                        ? t("runTicket.oilHaul")
                        : t("runTicket.waterHaul")
                        }: ${tankLevelEstimatedHaul} Barrels`}
                    </Box>

                    {item.tank.isDualPhase && Number.isFinite(interfaceLevelEstimatedHaul) &&
                        <Box>{`${t("runTicket.waterHaul")}: ${interfaceLevelEstimatedHaul} Barrels`}</Box>
                    }

                    {(item.purchaserName?.length ?? 0) > 0 &&
                        <Box>{`${t("runTicket.purchaser")}: ${item.purchaserName}`}</Box>
                    }

                    {(item.comments?.length ?? 0) > 0 &&
                        <Box>{`${t("runTicket.comments")}: ${item.comments}`}</Box>
                    }
                </Typography>
            </CardContent>
            <CardActions disableSpacing>
                <Button
                    // color="background.contrastText"

                    sx={{
                        //color: theme => theme.palette.getContrastText(theme.palette.background.paper)
                        color: "text.primary"
                    }}

                    variant="text"
                    onClick={toggleExpanded}
                    style={{ marginLeft: "auto" }}
                    endIcon={<ExpandMoreIcon sx={{
                        transform: !expanded ? "rotate(0deg)" : "rotate(180deg)",
                        transition: theme.transitions.create('transform', {
                            duration: theme.transitions.duration.shortest,
                        })
                    }} />}
                >
                    Details
                </Button>
            </CardActions>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <CardContent sx={{
                    padding: (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important`,
                }}>

                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                sm: 6,
                                lg: 4
                            }}>
                            <Typography variant="body1" sx={{ marginBottom: 1 }}>
                                {`${t("runTicket.firstMeasurement")}: ${format(new Date(item.startDateTime), "P pp")}`}
                            </Typography>
                            <Typography component="div" variant="body2">

                                <Box>
                                    {`${t("runTicket.topLevel")}: ${userState.userSettings?.tankLevelInches ? 
                                    item.tankLevelOpen + " inches" : getFeetAndInchesText(item.tankLevelOpen)}`}
                                </Box>

                                {item.tank.isDualPhase && Number.isFinite(item.interfaceLevelOpen) &&
                                    <Box>
                                        {`${t("runTicket.interfaceLevel")}: ${userState.userSettings?.tankLevelInches ? 
                                            item.tankLevelOpen  + " inches" : getFeetAndInchesText(item.interfaceLevelOpen as number)}`}
                                    </Box>
                                }

                                {Number.isFinite(item.temperatureTop) &&
                                    <Box>{`${t("runTicket.temperature")}: ${item.temperatureTop}`}</Box>
                                }

                                {(item.sealOff?.length ?? 0) > 0 &&
                                    <Box>{`${t("runTicket.sealOff")}: ${item.sealOff}`}</Box>
                                }

                            </Typography>
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                sm: 6,
                                lg: 4
                            }}>
                            <Typography variant="body1" sx={{ marginBottom: 1 }}>{`${t("runTicket.secondMeasurement")}: ${format(new Date(item.completeDateTime), "P pp")}`}</Typography>
                            <Typography component="div" variant="body2">

                                <Box>{`${t("runTicket.topLevel")}: ${userState.userSettings?.tankLevelInches ? 
                                    item.tankLevelOpen  + " inches" : getFeetAndInchesText(item.tankLevelClose)}`}</Box>

                                {item.tank.isDualPhase && Number.isFinite(item.interfaceLevelClose) &&
                                    <Box>{`${t("runTicket.interfaceLevel")}: ${userState.userSettings?.tankLevelInches ? 
                                    item.tankLevelOpen  + " inches" : getFeetAndInchesText(item.interfaceLevelClose as number)}`} </Box>
                                }

                                {Number.isFinite(item.temperatureBottom) &&
                                    <Box>{`${t("runTicket.temperature")}: ${item.temperatureBottom}`}</Box>
                                }

                                {(item.sealOn?.length ?? 0) > 0 &&
                                    <Box>{`${t("runTicket.sealOn")}: ${item.sealOn}`}</Box>
                                }

                            </Typography>
                        </Grid>

                        {(typeof item.specificGravity === "number" || typeof item.basicSedimentsAndWater === "number") &&
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 6,
                                    lg: 4
                                }}>
                                <Typography variant="body1" sx={{ marginBottom: 1 }}>{t("runTicket.condition")}</Typography>
                                <Typography component="div" variant="body2">
                                    {Number.isFinite(item.specificGravity) &&
                                        <Box>{`${t("runTicket.specificGravity")}: ${item.specificGravity}`}</Box>
                                    }
                                    {Number.isFinite(item.basicSedimentsAndWater) &&
                                        <Box>{`${t("runTicket.bsw")}: ${item.basicSedimentsAndWater}`}</Box>
                                    }
                                </Typography>
                            </Grid>
                        }

                        {item.attachments.length > 0 &&
                            <Grid size={12}>
                                <Typography variant="body1" sx={{ marginBottom: 1 }}>Attachments</Typography>
                                <AttachmentList attachments={item.attachments} onAction={onAttachmentAction} />
                            </Grid>
                        }
                    </Grid>
                </CardContent>
            </Collapse>
        </Card>
    );
}
